// Tabletter

import React, { createContext, useContext, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import {
 AppRoutes,
 CalculationHistory,
 MathOperation,
 PillsScreen,
 ResultOperation,
 ResultRow,
 UnitType,
} from '../types';

import { PillsContextType } from '../types/contexts';

import {
 getUnitTypeByString,
 getUnitOfMeasurementByUnitType,
 roundNumber,
 getValuesFromResultRows,
 getDescriptionsFromResultRows,
 convertUnit,
 getUnitsFromResultRows,
 getValuesPerWeightFromResultRows,
 getTabletterUnit,
} from '../utils';
import { CalculatorContext } from './CalculatorProvider';

import { ResultsContext } from './ResultsProvider';

export const PillsContext = createContext<PillsContextType>({
 setCurrentScreen: () => null,
 setPillsResultRows: () => null,
 pillsResultRows: [],
 onSelectChange: () => null,
 onValueChange: () => null,
 getValueInitialState: () => '',
 getSelectInitialState: () => '',
 selectedUnit: UnitType.Gram,
 setSelectedUnit: () => null,
 setCalculationHistory: () => null,
 loadHistory: () => null,
});

interface PillsProviderProps {
 children: React.ReactElement;
}

export function PillsProvider({
 children,
}: PillsProviderProps): React.ReactElement {
 const { checkboxValue, setCheckboxValue } = useContext(CalculatorContext);

 const navigation = useNavigate();

 const [currentScreen, setCurrentScreen] = useState(-1);

 const [pillsResultRows, setPillsResultRows] = useState<Array<ResultRow>>([
  { description: 'Dose' },
  { description: 'Pasientens vekt' },
  { description: 'Styrke pr tablett e.I.' },
 ]);

 const [pillsOperationsResult, setPillsOperationsResult] = useState<
  Array<ResultOperation>
 >([
  { firstValue: '', operation: MathOperation.times, secondValue: '' },
  { firstValue: '', operation: MathOperation.division, secondValue: '' },
 ]);

 const [pillsFinalResult, setPillsFinalResult] = useState(0);

 const [selectedUnit, setSelectedUnit] = useState<string>('');

 const [calculationHistory, setCalculationHistory] =
  useState<CalculationHistory>({
   title: 'Tabletter o.I.',
   redirectTo: AppRoutes.Pills,
  });

 const {
  setResultRows,
  setFinalResultValue,
  setResultOperations,
  setResultsCalculationHistory,
 } = useContext(ResultsContext);

 const saveHistory = () => {
  setCalculationHistory({
   ...calculationHistory,
   date: new Date(),
   data: {
    currentScreen: PillsScreen.Result,
    resultRows: pillsResultRows,
    operationResult: pillsOperationsResult,
    checkboxValue,
   },
  });
 };

 const loadHistory = (data: any) => {
  if (data.currentScreen) {
   setCurrentScreen(data.currentScreen);
  }
  if (data.resultRows) {
   setPillsResultRows(data.resultRows);
  }
  if (data.operationResult) {
   setPillsOperationsResult(data.operationResult);
  }
  if (data.checkboxValue) {
   setCheckboxValue(data.checkboxValue);
  }
  if (data.index !== undefined) {
   setCalculationHistory({
    ...calculationHistory,
    index: data.index,
   });
  }
 };

 const homeRedirect = () => () => {
  setCalculationHistory({ ...calculationHistory, index: undefined });
  setPillsResultRows([
   { description: 'Dose' },
   { description: 'Pasientens vekt' },
   { description: 'Styrke pr tablett e.I.' },
  ]);
  setCurrentScreen(-1);
  navigation(AppRoutes.Home);
 };

 const firstScreenRedirect = () => () => setCurrentScreen(PillsScreen.First);
 const secondScreenRedirect = () => () => setCurrentScreen(PillsScreen.Second);
 const thirdScreenRedirect = () => () => setCurrentScreen(PillsScreen.Third);
 const resultsRedirect = () => () => setCurrentScreen(PillsScreen.Result);

 const onSelectChange = (string: string) => {
  const unitType = getUnitTypeByString(string);
  const unitOfMeasurement = getUnitOfMeasurementByUnitType(unitType);
  setPillsResultRows(
   pillsResultRows?.map((input, index) =>
    index === currentScreen
     ? {
        ...input,
        calculatorValue: { ...input.calculatorValue, unit: unitOfMeasurement },
       }
     : input,
   ),
  );
 };

 const onValueChange = (value: string) => {
  setPillsResultRows(
   pillsResultRows?.map((input, index) =>
    index === currentScreen
     ? { ...input, calculatorValue: { ...input.calculatorValue, value } }
     : input,
   ),
  );
 };

 const getValueInitialState = () => {
  const value = pillsResultRows.find((input, index) => index === currentScreen)
   ?.calculatorValue?.value;
  return value ?? '';
 };

 const getSelectInitialState = () => {
  const value = pillsResultRows.find((input, index) => index === currentScreen)
   ?.calculatorValue?.unit?.description;
  return value ?? '';
 };

 const calculateResult = () => {
  const values = getValuesPerWeightFromResultRows(pillsResultRows);
  const newOperationsResult = [...pillsOperationsResult];
  if (!values[PillsScreen.First] || !values[PillsScreen.Third]) {
   return;
  }
  let partialResult = values[PillsScreen.First];
  if (values[PillsScreen.Second]) {
   partialResult =
    (values[PillsScreen.First] * values[PillsScreen.Second]) / 1000;
  }
  const result = roundNumber(partialResult / values[PillsScreen.Third]);
  setPillsFinalResult(result);
  newOperationsResult[1].resultValue = `${result} ${getTabletterUnit(result)}`;
  if (checkboxValue && newOperationsResult[0].secondValue?.includes('kg')) {
   newOperationsResult[0].firstValue += '/kg';
  }
  setPillsOperationsResult(newOperationsResult);
 };

 const calculateOperations = () => {
  const description = getDescriptionsFromResultRows(pillsResultRows);
  const values = getValuesFromResultRows(pillsResultRows);
  const units = getUnitsFromResultRows(pillsResultRows);
  const newOperationsResult = [...pillsOperationsResult];
  if (description[PillsScreen.First]) {
   newOperationsResult[0].firstValue = description[PillsScreen.First];
  }
  if (description[PillsScreen.Second]) {
   newOperationsResult[0].secondValue = description[PillsScreen.Second];
   const resultValue = convertUnit(
    values[PillsScreen.First] * values[PillsScreen.Second],
    units[PillsScreen.First],
    units[PillsScreen.Second],
   );
   newOperationsResult[0].resultValue = resultValue;
   newOperationsResult[1].firstValue = resultValue;
  } else {
   newOperationsResult[1].firstValue = description[PillsScreen.First];
  }
  if (description[PillsScreen.Third]) {
   newOperationsResult[1].secondValue = `${description[PillsScreen.Third]}/${
    UnitType.Tablett
   }`;
  }
  if (checkboxValue) {
   const unitTypeFirstLine = newOperationsResult[0].secondValue?.split(' ')[1];
   newOperationsResult[0].firstValue += `/${unitTypeFirstLine}`;
  } else {
   newOperationsResult[0].hide = true;
  }
 };

 useEffect(() => {
  setFinalResultValue(pillsFinalResult);
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [pillsFinalResult]);

 useEffect(() => {
  calculateResult();
  calculateOperations();
  setResultRows(pillsResultRows);
  saveHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [pillsResultRows]);

 useEffect(() => {
  setResultOperations(pillsOperationsResult);
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [pillsOperationsResult]);

 useEffect(() => {
  setPillsResultRows(
   pillsResultRows?.map((input, index) =>
    index > currentScreen ? { ...input, calculatorValue: undefined } : input,
   ),
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [currentScreen]);

 useEffect(() => {
  setResultsCalculationHistory(calculationHistory);
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [calculationHistory]);

 useEffect(() => {
  if (checkboxValue && pillsOperationsResult[0]) {
   const newOperationsResult = [...pillsOperationsResult];
   newOperationsResult[0].hide = false;
  }
 }, [checkboxValue]);

 return (
  <PillsContext.Provider
   // eslint-disable-next-line react/jsx-no-constructed-context-values
   value={{
    currentScreen,
    setCurrentScreen,
    homeRedirect,
    firstScreenRedirect,
    secondScreenRedirect,
    thirdScreenRedirect,
    resultsRedirect,
    pillsResultRows,
    setPillsResultRows,
    onSelectChange,
    onValueChange,
    getValueInitialState,
    getSelectInitialState,
    selectedUnit,
    setSelectedUnit,
    calculationHistory,
    setCalculationHistory,
    loadHistory,
   }}
  >
   {children}
  </PillsContext.Provider>
 );
}
